import {
  FacebookAuthProvider,
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signOut,
} from 'firebase/auth'
import { toast } from 'react-toastify'
import { verifyLogin } from './index'
import store, { resetStores } from 'src/store'
import { unregisterScreenFunction } from 'src/clinic/components/ClinicHomePage/hooks/useAumhumScreen'
import {
  markClientLogoutInProgress,
  notifyLocalAuthStateChanged,
} from 'src/shared/auth/logoutSignals'
import { rememberLastLoginHint } from 'src/shared/auth/lastLoginHint'
import { PORTAL_AVAILABILITY_OVERRIDES_STORAGE_KEY } from 'src/shared/config/portalAvailabilityRules'

const createSocialLoginHint = (method, user) => ({
  method,
  identifier: user?.email || user?.providerData?.find((provider) => provider?.email)?.email || '',
  displayName: user?.displayName || user?.phoneNumber || '',
})

export const handleGoogleLogin = async (
  handlePostLogin,
  setErrorText,
  doBasicOnboardingInside,
  setBasicOnboardingFlowVisible,
  setProfileDataForBasicOnboarding,
) => {
  const provider = new GoogleAuthProvider()
  const auth = getAuth()
  signInWithPopup(auth, provider)
    .then(async (result) => {
      // This gives you a Google Access Token. You can use it to access the Google API.
      //const credential = GoogleAuthProvider.credentialFromResult(result);
      const user = result?.user
      const firebaseIdToken = await user?.getIdToken()
      handlePostSocialLogin(
        handlePostLogin,
        firebaseIdToken,
        setErrorText,
        doBasicOnboardingInside,
        setBasicOnboardingFlowVisible,
        setProfileDataForBasicOnboarding,
        createSocialLoginHint('google', user),
      )
    })
    .catch((error) => {
      const errorMessage = error.message
      console.log(errorMessage)
      handlePostSocialLogin(handlePostLogin)
    })
}

export const handleFacebookLogin = async (
  handlePostLogin,
  setErrorText,
  doBasicOnboardingInside,
  setBasicOnboardingFlowVisible,
  setProfileDataForBasicOnboarding,
) => {
  const provider = new FacebookAuthProvider()
  const auth = getAuth()
  signInWithPopup(auth, provider)
    .then(async (result) => {
      // The signed-in user info.
      const user = result.user

      // This gives you a Facebook Access Token. You can use it to access the Facebook API.
      const credential = FacebookAuthProvider.credentialFromResult(result)
      const facebookAccessToken = credential.accessToken

      console.log(result)

      // Get Firebase ID token to send to our server
      const firebaseIdToken = await user?.getIdToken()

      handlePostSocialLogin(
        handlePostLogin,
        firebaseIdToken,
        setErrorText,
        doBasicOnboardingInside,
        setBasicOnboardingFlowVisible,
        setProfileDataForBasicOnboarding,
        createSocialLoginHint('facebook', user),
      )

      // IdP data available using getAdditionalUserInfo(result)
      // ...
    })
    .catch((error) => {
      // Handle Errors Here.
      const errorMessage = error.message
      console.log(errorMessage)
      handlePostSocialLogin(handlePostLogin)
    })
}

export const handleAppleLogin = async (
  handlePostLogin,
  setErrorText,
  doBasicOnboardingInside,
  setBasicOnboardingFlowVisible,
  setProfileDataForBasicOnboarding,
) => {
  const provider = new OAuthProvider('apple.com')
  const auth = getAuth()
  signInWithPopup(auth, provider)
    .then(async (result) => {
      // The signed-in user info.
      const user = result?.user
      // Apple credential
      // const credential = OAuthProvider.credentialFromResult(result);
      const firebaseIdToken = await user?.getIdToken()
      handlePostSocialLogin(
        handlePostLogin,
        firebaseIdToken,
        setErrorText,
        doBasicOnboardingInside,
        setBasicOnboardingFlowVisible,
        setProfileDataForBasicOnboarding,
        createSocialLoginHint('apple', user),
      )
    })
    .catch((error) => {
      // Handle Errors Here.
      const errorMessage = error.message
      console.log(errorMessage)
      handlePostSocialLogin(handlePostLogin)
    })
}

export const handlePostSocialLogin = async (
  handlePostLogin,
  accessToken,
  setErrorText,
  doBasicOnboardingInside,
  setBasicOnboardingFlowVisible,
  setProfileDataForBasicOnboarding,
  loginHint,
) => {
  if (!accessToken) {
    console.log('Error Signing In')
    toast.error('Invalid Social Login Attempt!')
    return
  }
  const response = await verifyLogin(accessToken)
  if (response?.status === 200 && loginHint) {
    rememberLastLoginHint(loginHint)
  }
  handlePostLogin(
    response,
    setErrorText,
    doBasicOnboardingInside,
    setBasicOnboardingFlowVisible,
    setProfileDataForBasicOnboarding,
  )
}

export const logoutHandler = (
  removeLocalStorageExceptKeys = [],
  removeSessionStorageExceptKeys = [],
) => {
  markClientLogoutInProgress()

  // todo later, not now: remove all,  except important
  let lanCode = sessionStorage.getItem('languageCode')
  let autoNavigatedToEmrOnceInSession
  if (
    removeSessionStorageExceptKeys.includes('autoNavigatedToEmrOnceInSession') &&
    sessionStorage.getItem('autoNavigatedToEmrOnceInSession')
  ) {
    autoNavigatedToEmrOnceInSession = sessionStorage.getItem('autoNavigatedToEmrOnceInSession')
  }

  const removeLocalStorageKeys = [
    'token',
    'accessTokenExpiry',
    'accessTokenExpiryReadable',
    'authorities',
    'FCMToken',
    'submittedFCMTokenForThisUser',
    'ROLE_CA_CONFIG',
    'ROLE_SCREEN_CONFIG',
    PORTAL_AVAILABILITY_OVERRIDES_STORAGE_KEY,
    'lastChosenFlow',
  ]
  if (removeLocalStorageExceptKeys.includes('ROLE_CA_CONFIG')) {
    unregisterScreenFunction()
  }
  
  // Preserve lastChosenFlow for dual-role users (kiosk or clinic-assistant)
  // This allows users with both ROLE_CLINIC_ASSISTANT and ROLE_AUMHUM_SCREEN
  // to be redirected to their last used portal instead of always defaulting to kiosk
  const lastChosenFlow = localStorage.getItem('lastChosenFlow')
  if (lastChosenFlow === 'kiosk' || lastChosenFlow === 'clinic-assistant') {
    if (!removeLocalStorageExceptKeys.includes('lastChosenFlow')) {
      removeLocalStorageExceptKeys.push('lastChosenFlow')
    }
  }
  removeLocalStorageKeys.forEach((key) => {
    if (!removeLocalStorageExceptKeys.includes(key)) {
      localStorage.removeItem(key)
    }
  })
  // localStorage.removeItem('onboardingType')

  console.log(autoNavigatedToEmrOnceInSession)

  sessionStorage.clear()
  sessionStorage.setItem(
    'languageCode',
    lanCode ? lanCode : navigator?.language || navigator?.userLanguage.split('-')[0],
  )
  markClientLogoutInProgress()
  if (autoNavigatedToEmrOnceInSession) {
    sessionStorage.setItem('autoNavigatedToEmrOnceInSession', autoNavigatedToEmrOnceInSession)
  }
  // Sign out from Firebase if user is authenticated
  const auth = getAuth()
  if (auth.currentUser) {
    signOut(auth).catch((error) => {
      // Silently handle any errors during Firebase sign out
      console.warn('Error signing out from Firebase:', error)
    })
  }
  
  store.dispatch(resetStores())
  // store.dispatch(getSetupData())
  notifyLocalAuthStateChanged()
}
